import { BaseFilterComponent } from './types';
import { defineMessages, useIntl } from 'react-intl';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { NumericRange } from './NumericRange';
import { PercentageRange } from './PercentageRange';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import * as React from 'react';

const messages = defineMessages({
  title: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.title',
    defaultMessage: 'Combined In Silico Prediction',
  },
  percentDamaging: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.percentDamaging',
    defaultMessage: '% Damaging',
  },
  rankScore: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.rankScore',
    defaultMessage: 'Damaging Rank Score',
  },
  selectOne: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.selectOne',
    defaultMessage: 'Select an option',
  },
  maxOption: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.maxOption',
    defaultMessage: '<=',
  },
  minOption: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.minOption',
    defaultMessage: '>=',
  },
  helpMessage: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.helpMessage',
    defaultMessage: 'Decimal value: 0.0 - 1.0',
  },
  addFilter: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.addFilter',
    defaultMessage: 'Add Filter',
  },
  originFormat: {
    id: 'omicsExplorer.filterResultsManager.components.combinedInSilicoPrediction.originFormat',
    defaultMessage: '{origin} / {selection}',
  },
});

type SelectOptionType = 'percentDamaging' | 'rankScore';

export const CombinedInSilicoPrediction: React.FC<BaseFilterComponent> = ({
  onFilterAdd,
  onFilterRemove,
  origin,
}) => {
  const { formatMessage } = useIntl();
  const [selectedOption, setSelectedOption] =
    React.useState<SelectOptionType>('percentDamaging');

  const handleOnFilterAdd = (filter: any) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    onFilterAdd(filter);
  };
  const handleOnFilterRemove = (filter: any) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    onFilterRemove(filter);
  };

  return (
    <FormBox>
      <Select
        label={formatMessage(messages.selectOne)}
        value={selectedOption}
        onChange={opt => setSelectedOption(opt as SelectOptionType)}
        fullWidth
      >
        <SelectOption
          title={formatMessage(messages.percentDamaging)}
          value="percentDamaging"
        />
        <SelectOption
          title={formatMessage(messages.rankScore)}
          value="rankScore"
        />
      </Select>

      {selectedOption === 'percentDamaging' ? (
        <PercentageRange
          id="omicsExplorer.filters.combinedInSilicoPrediction.dbnsfpDamagingVote"
          title={messages.title}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          content={null}
          origin={formatMessage(messages.originFormat, {
            origin,
            selection: formatMessage(messages.percentDamaging),
          })}
          injectedProps={{
            id: selectedOption,
            searchKey: 'dbnsfpDamagingVote',
          }}
          onFilterAdd={handleOnFilterAdd}
          onFilterRemove={handleOnFilterRemove}
        />
      ) : (
        <NumericRange
          id="omicsExplorer.filters.combinedInSilicoPrediction.dbnsfpMeanRankscore"
          title={messages.title}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          content={null}
          origin={formatMessage(messages.originFormat, {
            origin,
            selection: formatMessage(messages.rankScore),
          })}
          injectedProps={{
            id: selectedOption,
            searchKey: 'dbnsfpMeanRankscore',
            maximumOption: {
              id: 'omicsExplorer.filters.variantAlleleFrequency.lessThan',
              placeholderIntlLabel: messages.maxOption,
            },
            minimumOption: {
              id: 'omicsExplorer.filters.variantAlleleFrequency.greaterThan',
              placeholderIntlLabel: messages.minOption,
            },
            buttonIntlLabel: messages.addFilter,
            maximumValue: 1,
            minimumValue: 0,
            step: 0.01,
            maxHelpMessage: messages.helpMessage,
            minHelpMessage: messages.helpMessage,
          }}
          onFilterAdd={handleOnFilterAdd}
          onFilterRemove={handleOnFilterRemove}
        />
      )}
    </FormBox>
  );
};
